import { useState, type FormEvent } from 'react'
import {
  formatEur,
  formatQty,
  marginCents,
  marginPercent,
  parsePriceCents,
  parseQtyTenths,
  STOCK_UNITS,
  VAT_RATES,
  type InventoryPartDTO,
} from '@wf/shared'
import { useAdjustStock, useArchivePart, useCreatePart, usePatchPart, useReactivatePart } from './useInventory'

// Cent → Euro-Eingabewert (4500 → "45"); leere/0-Werte als "" bzw. "0".
const centsToInput = (cents: number): string => String((cents || 0) / 100)

interface FieldState {
  partNumber: string
  name: string
  unit: string
  stock: string // nur Anlegen (Anfangsbestand); im Edit über „Bestand korrigieren"
  safetyStock: string
  purchase: string
  selling: string
  vatRate: number
  manufacturer: string
  storageLocation: string
  barcode: string
}

function initialFields(part?: InventoryPartDTO): FieldState {
  return {
    partNumber: part?.partNumber ?? '',
    name: part?.name ?? '',
    unit: part?.unit ?? 'Stück',
    stock: part ? formatQty(part.stockTenths) : '0',
    safetyStock: part ? formatQty(part.safetyStockTenths) : '0',
    purchase: part ? centsToInput(part.purchasePriceCents) : '',
    selling: part ? centsToInput(part.sellingPriceCents) : '',
    vatRate: part?.vatRate ?? 19,
    manufacturer: part?.manufacturer ?? '',
    storageLocation: part?.storageLocation ?? '',
    barcode: part?.barcode ?? '',
  }
}

/**
 * Anlege-/Bearbeiten-Formular für ein Lager-Teil (Inline-Panel). Anlegen: alle Felder inkl. Anfangsbestand;
 * gehört die SNR einem archivierten Teil, bietet das Formular „Reaktivieren?" an. Bearbeiten: Stammdaten per OCC,
 * Bestand NICHT direkt (separat über „Bestand korrigieren" → adjustStock), plus „Archivieren".
 */
export function PartForm({ part, onClose }: { part?: InventoryPartDTO; onClose: () => void }) {
  const isEdit = !!part
  const [f, setF] = useState<FieldState>(() => initialFields(part))
  const [error, setError] = useState<string | null>(null)
  const [archivedHit, setArchivedHit] = useState<InventoryPartDTO | null>(null)
  const [correctTo, setCorrectTo] = useState('')

  const create = useCreatePart()
  const patch = usePatchPart()
  const reactivate = useReactivatePart()
  const archive = useArchivePart()
  const adjust = useAdjustStock()

  const set = (k: keyof FieldState, v: string | number) => setF((prev) => ({ ...prev, [k]: v }))

  const purchaseCents = parsePriceCents(f.purchase)
  const sellingCents = parsePriceCents(f.selling)
  const margin = marginCents(sellingCents, purchaseCents)
  const marginPct = marginPercent(sellingCents, purchaseCents)

  function stammdaten() {
    return {
      partNumber: f.partNumber.trim(),
      name: f.name.trim(),
      unit: f.unit,
      safetyStockTenths: parseQtyTenths(f.safetyStock),
      purchasePriceCents: purchaseCents,
      sellingPriceCents: sellingCents,
      vatRate: f.vatRate,
      manufacturer: f.manufacturer.trim() || null,
      storageLocation: f.storageLocation.trim() || null,
      barcode: f.barcode.trim() || null,
    }
  }

  function submit(event: FormEvent) {
    event.preventDefault()
    setError(null)
    if (!f.partNumber.trim()) return setError('Teilenummer (SNR) fehlt')
    if (!f.name.trim()) return setError('Bezeichnung fehlt')

    if (isEdit && part) {
      patch.mutate(
        { id: part.id, body: { version: part.version, ...stammdaten() } },
        { onSuccess: onClose, onError: (e) => setError(e instanceof Error ? e.message : 'Speichern fehlgeschlagen') },
      )
      return
    }
    create.mutate(
      { ...stammdaten(), stockTenths: parseQtyTenths(f.stock) },
      {
        onSuccess: (res) => {
          if ('archived' in res) setArchivedHit(res.part) // SNR gehört archiviertem Teil → reaktivieren anbieten
          else onClose()
        },
        onError: (e) => setError(e instanceof Error ? e.message : 'Anlegen fehlgeschlagen'),
      },
    )
  }

  // Pop-up-Ersatz: SNR gehört einem archivierten Teil → Reaktivierung anbieten.
  if (archivedHit) {
    return (
      <div className="wf-card wf-card--static">
        <p>
          Ein archiviertes Teil mit dieser Teilenummer existiert bereits ({archivedHit.name}). Reaktivieren und mit den
          neuen Daten aktualisieren?
        </p>
        <div className="wf-listfoot">
          <button
            type="button"
            className="wf-btn"
            disabled={reactivate.isPending}
            onClick={() =>
              reactivate.mutate({ id: archivedHit.id, body: stammdaten() }, { onSuccess: onClose })
            }
          >
            Teil reaktivieren
          </button>
          <button type="button" className="wf-btn wf-btn--ghost" onClick={onClose}>
            Abbrechen
          </button>
        </div>
      </div>
    )
  }

  return (
    <form className="wf-settings" onSubmit={submit}>
      <h3 className="wf-sectionhead">{isEdit ? `Teil bearbeiten: ${part?.name}` : 'Neues Teil'}</h3>
      {error ? (
        <div className="error" role="alert">
          {error}
        </div>
      ) : null}

      <div className="two">
        <label className="wf-field">
          <span className="wf-label">Teilenummer (SNR) *</span>
          <input className="wf-input" value={f.partNumber} onChange={(e) => set('partNumber', e.target.value)} />
        </label>
        <label className="wf-field">
          <span className="wf-label">Bezeichnung *</span>
          <input className="wf-input" value={f.name} onChange={(e) => set('name', e.target.value)} />
        </label>
      </div>

      <div className="two">
        <label className="wf-field">
          <span className="wf-label">Einheit</span>
          <select className="wf-input" value={f.unit} onChange={(e) => set('unit', e.target.value)}>
            {STOCK_UNITS.map((u) => (
              <option key={u} value={u}>
                {u}
              </option>
            ))}
          </select>
        </label>
        <label className="wf-field">
          <span className="wf-label">MwSt</span>
          <select className="wf-input" value={f.vatRate} onChange={(e) => set('vatRate', Number(e.target.value))}>
            {VAT_RATES.map((v) => (
              <option key={v} value={v}>
                {v}%
              </option>
            ))}
          </select>
        </label>
      </div>

      <div className="two">
        <label className="wf-field">
          <span className="wf-label">Einkaufspreis netto (€)</span>
          <input className="wf-input wf-tnum" inputMode="decimal" value={f.purchase} onChange={(e) => set('purchase', e.target.value)} />
        </label>
        <label className="wf-field">
          <span className="wf-label">Verkaufspreis netto (€)</span>
          <input className="wf-input wf-tnum" inputMode="decimal" value={f.selling} onChange={(e) => set('selling', e.target.value)} />
        </label>
      </div>

      <p className="sub">
        Marge: {formatEur(margin)} {marginPct != null ? `(${marginPct}%)` : '(–)'}
      </p>

      <div className="two">
        {!isEdit ? (
          <label className="wf-field">
            <span className="wf-label">Anfangsbestand</span>
            <input className="wf-input wf-tnum" inputMode="decimal" value={f.stock} onChange={(e) => set('stock', e.target.value)} />
          </label>
        ) : (
          <div className="wf-field">
            <span className="wf-label">Bestand</span>
            <div className="muted">
              {formatQty(part!.stockTenths)} {part!.unit} (Änderung über „Bestand korrigieren")
            </div>
          </div>
        )}
        <label className="wf-field">
          <span className="wf-label">Sicherheitsbestand</span>
          <input
            className="wf-input wf-tnum"
            inputMode="decimal"
            value={f.safetyStock}
            onChange={(e) => set('safetyStock', e.target.value)}
          />
        </label>
      </div>

      <div className="two">
        <label className="wf-field">
          <span className="wf-label">Hersteller/Lieferant</span>
          <input className="wf-input" value={f.manufacturer} onChange={(e) => set('manufacturer', e.target.value)} />
        </label>
        <label className="wf-field">
          <span className="wf-label">Lagerort</span>
          <input className="wf-input" value={f.storageLocation} onChange={(e) => set('storageLocation', e.target.value)} />
        </label>
      </div>
      <label className="wf-field">
        <span className="wf-label">Barcode/EAN</span>
        <input className="wf-input" value={f.barcode} onChange={(e) => set('barcode', e.target.value)} />
      </label>

      <div className="wf-listfoot">
        <button type="submit" className="wf-btn" disabled={create.isPending || patch.isPending}>
          Speichern
        </button>
        <button type="button" className="wf-btn wf-btn--ghost" onClick={onClose}>
          Abbrechen
        </button>
        {isEdit && part ? (
          <button
            type="button"
            className="wf-btn wf-btn--ghost"
            disabled={archive.isPending}
            onClick={() => {
              if (confirm(`Teil „${part.name}" archivieren?`)) archive.mutate(part.id, { onSuccess: onClose })
            }}
          >
            Archivieren
          </button>
        ) : null}
      </div>

      {isEdit && part ? (
        <div className="wf-line-add">
          <span className="wf-label">Bestand korrigieren auf</span>
          <input
            className="wf-input wf-tnum"
            inputMode="decimal"
            placeholder={formatQty(part.stockTenths)}
            value={correctTo}
            onChange={(e) => setCorrectTo(e.target.value)}
          />
          <button
            type="button"
            className="wf-btn wf-btn--ghost"
            disabled={adjust.isPending || correctTo.trim() === ''}
            onClick={() =>
              adjust.mutate(
                { id: part.id, body: { stockTenths: parseQtyTenths(correctTo) } },
                { onSuccess: () => setCorrectTo('') },
              )
            }
          >
            Korrigieren
          </button>
        </div>
      ) : null}
    </form>
  )
}
